import { useMemo, useState, useEffect, useRef } from "react";
import { EU_COUNTRIES, OSS_THRESHOLD_EUR, findCountry } from "../data/vatData";
import { fmtEUR } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { trackOssThresholdAbove } from "../lib/analytics";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Progress } from "./ui/progress";
import { Gauge } from "lucide-react";

export default function OssTracker() {
    const { t } = useI18n();
    const [home, setHome] = useState("LV");
    const [sales, setSales] = useState("3500");

    const country = useMemo(() => findCountry(home), [home]);
    const total = parseFloat(sales) || 0;
    const pct = Math.min(100, (total / OSS_THRESHOLD_EUR) * 100);
    const above = total > OSS_THRESHOLD_EUR;

    // Analytics: fire once when crossing threshold (not on every keystroke above).
    const wasAbove = useRef(above);
    useEffect(() => {
        if (above && !wasAbove.current) trackOssThresholdAbove(home, total);
        wasAbove.current = above;
    }, [above, home, total]);

    return (
        <section data-testid="oss-tracker-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4">
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">
                        {t("oss.eyebrow")}
                    </div>
                    <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
                        {t("oss.titleLine1")}
                        <span className="text-[#10B981]">{t("oss.titleHighlight")}</span>
                        {t("oss.titleLine2")}
                    </h2>
                    <p className="mt-3 text-slate-600 leading-relaxed">{t("oss.intro")}</p>
                </div>

                <div data-testid="oss-tracker-card" className="lg:col-span-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                            <Gauge className="h-4 w-4" />
                        </span>
                        <h3 className="font-display text-lg font-bold text-slate-900">{t("oss.cardTitle")}</h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                {t("oss.homeCountry")}
                            </Label>
                            <Select value={home} onValueChange={setHome}>
                                <SelectTrigger data-testid="oss-home-country" className="h-11 mt-2 border-slate-200">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {EU_COUNTRIES.map((c) => (
                                        <SelectItem key={c.code} value={c.code}>
                                            <span className="mr-2">{c.flag}</span> {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                {t("oss.sales")}
                            </Label>
                            <div className="mt-2 relative">
                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                                <Input
                                    data-testid="oss-sales-input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={sales}
                                    onChange={(e) => setSales(e.target.value)}
                                    className="h-11 pl-7 border-slate-200 focus-visible:ring-[#10B981] focus-visible:border-[#10B981]"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-7">
                        <div className="flex items-end justify-between">
                            <div>
                                <div className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                    {t("oss.thresholdUsed")}
                                </div>
                                <div className="font-display text-2xl font-black text-slate-900 mt-1" data-testid="oss-threshold-used">
                                    {fmtEUR(total)}{" "}
                                    <span className="text-slate-400 font-medium text-base">/ {fmtEUR(OSS_THRESHOLD_EUR)}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-display text-2xl font-black text-slate-900" data-testid="oss-progress-pct">
                                    {pct.toFixed(1)}%
                                </div>
                                <div className="text-xs text-slate-500">{t("oss.ofCap")}</div>
                            </div>
                        </div>
                        <Progress
                            data-testid="oss-progress-bar"
                            value={pct}
                            className={`mt-3 h-3 bg-slate-100 ${above ? "[&>div]:bg-rose-500" : "[&>div]:bg-[#10B981]"}`}
                        />
                    </div>

                    <div
                        data-testid={above ? "oss-status-above" : "oss-status-below"}
                        className={`mt-5 rounded-md border p-4 ${
                            above
                                ? "bg-rose-50 border-rose-200 text-rose-900"
                                : "bg-emerald-50 border-emerald-200 text-emerald-900"
                        }`}
                    >
                        <div className="flex items-center gap-2 font-semibold">
                            <span className="text-lg">{above ? "🔴" : "🟢"}</span>
                            {above ? t("oss.above") : t("oss.below")}
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                            {above
                                ? t("oss.aboveDesc", { country: country?.name })
                                : t("oss.belowDesc", { flag: country?.flag, country: country?.name })}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
